import { toast } from 'react-hot-toast';
import Axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthHeader } from '../hooks/useAuthHeader';

const getBaseUrl = () => {
  // Get the current hostname
  const hostname = window.location.hostname;
  
  // Development environment
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
    //return 'https://api.bvtbackend.com/api';
  }
  
  // Production environment (AWS Elastic Beanstalk)
  return 'https://api.bvtbackend.com/api';
};

const axios = Axios.create({
  baseURL: '/', //getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout configuration
  timeout: 60000
});

// Flag to prevent multiple token refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const {removeAuthHeader} = useAuthHeader();

    // Handle specific error status codes
    if (error.response) {
      switch (error.response.status) {
        case 204:
          // No Content
          toast.error(error.response.data?.message || 'Requested data not available');
          break;
        case 400:
          // Bad Request
          toast.error(error.response.data?.message || 'Invalid request');
          break;
        case 404:
          // Not Found
          toast.error(error.response.data?.message || 'Requested data not available');
          break;
        case 401:
          // Token expired or invalid
          if (!originalRequest._retry && !isRefreshing) {
            originalRequest._retry = true;
            isRefreshing = true;

            try {
              // Try to refresh the token
              const response = await axios.post('/heartbeat');
              if (response.data && response.data.token) {
                // Update stored user and auth header
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = { ...currentUser, token: response.data.token };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                const { setAuthHeader } = await import('../hooks/useAuthHeader');
                setAuthHeader(response.data.token);
                
                // Update the original request with new token
                originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
                
                // Process queued requests
                processQueue(null, response.data.token);
                
                // Retry the original request
                return axios(originalRequest);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              processQueue(refreshError, null);
              
              // Token refresh failed, logout user
              toast.error('Session expired. Please login again.');
              removeAuthHeader();
              localStorage.removeItem('user');
              window.location.href = '/login';
              return Promise.reject(refreshError);
            } finally {
              isRefreshing = false;
            }
          } else if (isRefreshing) {
            // Queue the request if refresh is in progress
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          } else {
            // Refresh already failed, logout
            toast.error('Session expired. Please login again.');
            removeAuthHeader();
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
          break;
        case 403:
          toast.error('Access Denied: You do not have permission to perform this action.');
          break;
        case 404:
          toast.error('Resource not found. Please check your request.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          // Other errors
          toast.error(error.response.data?.message || 'Something went wrong. Please try again after sometime.');
      }
    } else if (error.code === 'ECONNABORTED') {
      // Request timeout
      toast.error('Request timed out. Please try again.');
    } else if (error.request) {
      // Network Error
      toast.error('Network Error. Please check your connection.');
    } else {
      // Other Error
      toast.error('An unexpected error occurred. Please try again after sometime.');
    }
    
    // Log the error for debugging
    console.error('API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message,
      error: error
    });

    // Reject the promise with the error response
    return Promise.reject(error.response || error);
  }
);

axios.interceptors.request.use((config) => {
  const {getAuthToken} = useAuthHeader();
  if (getAuthToken() !== null && getAuthToken() !== "null") {
    config.headers.Authorization = `Bearer ${getAuthToken()}`;
  }
  return config;
});

export default axios;